import { XStack, type XStackProps } from 'tamagui';

import { Icon, type IconType } from '../icons/Icon';
import { BodyMediumRegular } from '../text/Typography';

interface SelectMultipleItemProps {
    title: string;
    icon?: IconType;
    disabled?: boolean;
    selected?: boolean;
    onPress: () => void;
}

const SelectMultipleItem = ({
    title,
    icon,
    disabled = false,
    selected = false,
    onPress,
}: SelectMultipleItemProps) => {
    return (
        <XStack
            alignItems="center"
            gap={8}
            paddingHorizontal={16}
            paddingVertical={8}
            minHeight={48}
            alignSelf="flex-start"
            borderWidth={2}
            borderRadius={16}
            borderColor={
                disabled ? '$gray200' : selected ? '$black' : '$gray200'
            }
            backgroundColor={disabled ? '$gray100' : '$white'}
            hoverStyle={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderColor: disabled
                    ? '$gray200'
                    : selected
                      ? '$black'
                      : '$gray300',
            }}
            pressStyle={{
                scale: disabled ? 1 : 0.99,
                borderColor: disabled ? '$gray200' : '$black',
            }}
            animation="button"
            animateOnly={['borderColor', 'backgroundColor', 'transform']}
            onPress={disabled ? () => {} : onPress}
            opacity={disabled ? 0.6 : 1}
        >
            {icon != null && (
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={20}
                    height={20}
                >
                    <Icon
                        icon={icon}
                        size={20}
                        color={disabled ? '$gray1000' : '$black'}
                    />
                </XStack>
            )}
            <XStack alignItems="center" justifyContent="center">
                <BodyMediumRegular color={disabled ? '$gray1000' : '$black'}>
                    {title}
                </BodyMediumRegular>
            </XStack>
        </XStack>
    );
};

interface SelectMultipleProps {
    options: Array<{
        title: string;
        icon?: IconType;
        disabled?: boolean;
    }>;
    selectedIndices?: number[] | null;
    onSelect: (index: number) => void;
    containerProps?: XStackProps;
}

export const SelectMultiple = ({
    options,
    selectedIndices,
    onSelect,
    containerProps,
}: SelectMultipleProps) => {
    return (
        <XStack gap={12} flexWrap="wrap" {...containerProps}>
            {options.map((option, index) => (
                <SelectMultipleItem
                    key={index}
                    title={option.title}
                    icon={option.icon}
                    disabled={option.disabled}
                    selected={selectedIndices?.includes(index) ?? false}
                    onPress={() => {
                        onSelect(index);
                    }}
                />
            ))}
        </XStack>
    );
};
